import { v4 as uuidv4 } from 'uuid';
import { firestore } from 'firebase';
import { fireStore, firebase } from 'src/firebase';
import StorageHelper from 'src/firebase/db/StorageHelper';
import { Status } from 'src/constants/Interface';

export type APISuccessfulResponse<T> = {
  status: Status.SUCCESS;
  data: T;
};
export type APIFailedResponse = {
  status: Status.FAILURE;
  error: {
    code: number;
    message: string;
  };
};

class DatabaseAdapter {
  private schema: string;
  schemaKeys: string[];

  constructor(props: any) {
    this.schema = props.schema;
    this.schemaKeys = [];
  }

  filterQuery<T>(query: Partial<T> & { [key: string]: any }, schemaKeys?: string[]): Partial<T> {
    const keys = schemaKeys ? schemaKeys : this.schemaKeys;
    for (let key in query) {
      const isValid = keys.includes(key);
      if (!isValid) delete query[key];
    }
    return query;
  }

  setSchemaKeys(keys: string[]) {
    this.schemaKeys = keys;
  }
  /**
   * Refernce型からIDだけ抽出
   */
  extractIdFromRef = (v: firestore.DocumentReference[]) => {
    const formatted: string[] = v.map((ref) => {
      return ref.id ? ref.id : ('' as string);
    });
    return formatted;
  };

  /**
   * Refernce型からDataを抽出
   */
  extractDataFromRef = (v: firestore.DocumentReference[]) => {
    const formatted = v.map(async (ref) => {
      return await ref.get().then((snapshot) => {
        return snapshot.data();
      });
    });
    return formatted;
  };

  getAuthUser() {
    return firebase.auth().currentUser;
  }

  static startTransaction() {
    return fireStore.batch();
  }

  static endTransaction(batch: any) {
    return batch.commit();
  }

  getServerTimeStamp = () => {
    return firebase.firestore.FieldValue.serverTimestamp();
  };

  getDocumentRef = (document_id: string) => {
    const docRef = fireStore.collection(this.schema).doc(document_id);
    return docRef;
  };

  getConnection(schema?: string) {
    const c = fireStore.collection(schema ? schema : this.schema);
    return c;
  }

  createAutoDocument(payload: {}) {
    return fireStore.collection(this.schema).doc().set(payload);
  }

  createDocument(document_id: string, payload: {}) {
    const docRef = this.getDocumentRef(document_id);
    return docRef.set(payload);
  }

  updateDocument(document_id: string, payload: {}) {
    const docRef = this.getDocumentRef(document_id);
    docRef.update(payload);
  }

  getDocument(document_id: string) {
    const docRef = this.getDocumentRef(document_id);
    return docRef
      .get()
      .then((doc) => {
        if (doc.exists) {
          return doc.data();
        } else {
          console.log('No such document!');
          return null;
        }
      })
      .catch(function (error) {
        console.log('Error getting document:', error);
        return error;
      });
  }

  async uploadImage(imagePath: string): Promise<string | undefined> {
    const storagePaths = await this.uploadImages([imagePath]);
    return storagePaths.length > 0 ? storagePaths[0] : undefined;
  }

  uploadImages(imagePaths: string[]): Promise<string[]> {
    const isBase64 = (path: string) => path.match(/^data:/);
    const base64ToBlob = (path: string) => {
      const bin = atob(path.replace(/^.*,/, ''));
      const mineType = path.slice(path.indexOf(':') + 1, path.indexOf(';'));
      const buffer = new Uint8Array(bin.length).map((_, i) => bin.charCodeAt(i));
      return new Blob([buffer.buffer], {
        type: mineType || 'image/jpeg',
      });
    };
    return Promise.all(
      imagePaths.map(async (path, idx) => {
        if (!isBase64(path)) return path;
        const blob = base64ToBlob(path);
        const storage = new StorageHelper({
          contentType: blob.type,
        });
        // TODO: 画像のアップロードが失敗した場合、ユーザーにアラートを表示させる必要がある
        return await storage.upload(`shop/${uuidv4()}`, blob);
      }),
    );
  }

  throwErrorIfUserNotAuth() {
    const user = this.getAuthUser();
    if (user) {
      return this.getDocument(user.uid);
    } else {
      return this.throwError(41, '認証されていないユーザーです');
    }
  }

  formatSuccess<T>(data: T): APISuccessfulResponse<T> {
    return {
      status: Status.SUCCESS,
      data,
    };
  }

  throwError(statusCode: number, errMessage: string): APIFailedResponse {
    return {
      status: Status.FAILURE,
      error: {
        code: statusCode,
        message: errMessage,
      },
    };
  }
}

export default DatabaseAdapter;
